import React, { memo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';

import { Icon, Spinner } from 'components';
import Tooltip from 'components/Tooltip';
import { DropdownV3, InputV2 } from 'components/ComponentV2';
import InputDropdownCombo from 'components/ComponentV2/InputDropdownCombo';
import useScrollOffset from 'hooks/useScrollOffset';
import { BasicInfo } from 'hooks/useCandidate';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { UniversityAttributes } from 'lib/models/university';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { useCommonList } from 'lib/contexts/common-list-context';
import { useAuth } from 'lib/providers/AuthProvider';
import { Footer } from '../Footer/Footer.component';

import styles from '../Onboarding.module.scss';

export interface EducationProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  onNextClick: ({
    candidateInfo,
    isBackClicked,
  }: {
    candidateInfo: BasicInfo;
    isBackClicked?: boolean;
  }) => void;
  isLoading: boolean;
  isBackLoading: boolean;
}

export const Education: React.FC<EducationProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    onNextClick,
    isLoading,
    isBackLoading,
  }: EducationProps) => {
    const [tCommon] = useTranslation('common');
    const [t] = useTranslation('candidate-onboarding');
    const { candidate } = useAuth();
    const contentClassNames = cn(styles.container, className);

    const { universities, subjects, degrees, graduateDateOptions } =
      useCommonList();

    /** Add reducers for updating candidate Info */
    const [candidateInfo, setCandidateInfo] = useState<BasicInfo>({
      first_name: '',
      last_name: '',
      educationHistoriesId: '',
      university_id: '',
      university_name: '',
      degree_id: '',
      degree_label: '',
      subject_id: '',
      subject_name: '',
      degree_name: '',
      graduation_year: new Date().getFullYear(),
      education_type: 'university',
    });
    const [showDegreeName, setShowDegreeName] = useState(false);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);
    const degreeDropdownRef = useRef<HTMLButtonElement>(null);
    const subjectDropdownRef = useRef<HTMLButtonElement>(null);
    const universityDropdownRef = useRef<HTMLInputElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const graduateYearDropdownRef = useRef<HTMLButtonElement>(null);
    const [fullyScrolled, setFullyScrolled] = useState(false);

    const { getScrollOffset, isWindowScrolled } = useScrollOffset();

    useEffect(() => {
      if (candidate) {
        const {
          first_name,
          last_name,
          graduation_year,
          education_histories: educationHistoriesResponse,
        } = candidate;

        setCandidateInfo((info) => ({
          ...info,
          first_name,
          last_name,
          graduation_year,
        }));

        const educationHistories = parseArrayResponse(
          educationHistoriesResponse
        )?.filter((item) => item.education_type === 'university');

        if (educationHistories?.length > 0) {
          const {
            id: educationHistoriesId,
            university: universityResponse,
            university_name,
            subject: subjectResponse,
            degree: degreeResponse,
            degree_name,
          } = educationHistories[0];
          const university =
            parseResponse<UniversityAttributes>(universityResponse);
          const degree = parseResponse(degreeResponse);
          const subject = parseResponse(subjectResponse);

          setCandidateInfo((info) => ({
            ...info,
            educationHistoriesId,
            university_id: university?.id,
            university_name: university_name || university?.name,
            degree_id: degree?.id,
            degree_label: degree?.name,
            subject_id: subject?.id,
            subject_name: subject?.name,
            degree_name: degree_name || '',
            education_type: 'university',
          }));
          if (degree_name) {
            setShowDegreeName(true);
          }
        }
      }
    }, [candidate]);

    useVisibleOnIntersect({
      elRef: bottomRef,
      threshold: 0,
      onIntersect: () => setFullyScrolled(true),
      onHidden: () => setFullyScrolled(false),
      rootMargin: '0px',
    });

    const isReady = !!(
      candidateInfo.university_name &&
      candidateInfo.degree_id &&
      candidateInfo.subject_id &&
      candidateInfo.first_name &&
      candidateInfo.last_name &&
      candidateInfo.graduation_year
    );

    const onSubmit = (isBackClicked?: boolean) => {
      onNextClick({ candidateInfo, isBackClicked });
    };

    const scrollToDropdown = (dropdownRef: React.RefObject<HTMLElement>) => {
      const topSpace = 12;
      const offset = getScrollOffset(
        dropdownRef,
        containerRef,
        headerRef,
        topSpace
      );
      window.scrollTo({ top: offset, behavior: 'smooth' });
    };

    const scrollToDropdownDegree = () => {
      scrollToDropdown(degreeDropdownRef);
    };

    const scrollToDropdownSubject = () => {
      scrollToDropdown(subjectDropdownRef);
    };

    const scrollToDropdownUniversity = () => {
      scrollToDropdown(universityDropdownRef);
    };

    const onUniversitySelect = (value: string) => {
      const university = universities.find(
        (university) => university.label.toLowerCase() === value.toLowerCase()
      );
      setCandidateInfo((info) => ({
        ...info,
        university_id: university?.id || '',
        university_name: university?.label || value,
      }));
    };

    const onDegreeSelect = (value: string) => {
      const degree = degrees.find((degree) => degree.id === value);
      if (degree) {
        setCandidateInfo((info) => ({
          ...info,
          degree_id: degree.id,
          degree_label: degree.label,
        }));
      }
    };

    const onSubjectSelect = (value: string) => {
      const subject = subjects.find((subject) => subject.id === value);
      if (subject) {
        setCandidateInfo((info) => ({
          ...info,
          subject_id: subject.id,
          subject_name: subject.label,
        }));
      }
    };

    if (!universities || !degrees || !subjects) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    }

    const scrollToCurrentYear = () => {
      if (!graduateYearDropdownRef.current) return;

      const graduateYearDropdown = graduateYearDropdownRef.current;
      if (graduateYearDropdown.children[1].getAttribute('aria-expanded')) {
        const graduationOptions =
          graduateYearDropdown.children[2]?.children[0]?.children;
        if (graduationOptions) {
          const currentYear = new Date().getFullYear();
          const currentYearOption = Array.from(graduationOptions).filter(
            (ele) => ele.firstChild?.textContent === currentYear.toString()
          )[0];
          currentYearOption.scrollIntoView();
        }
      }
    };

    useEffect(() => {
      if (!graduateYearDropdownRef.current) return;

      const observer = new MutationObserver(scrollToCurrentYear);
      observer.observe(graduateYearDropdownRef.current, { childList: true });
      return () => observer.disconnect();
    }, [graduateYearDropdownRef.current]);

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="Education"
        ref={containerRef}
      >
        <div className={styles.content}>
          <p ref={headerRef} className={styles.setup}>
            {t('education-demographic-detail.basic-info')}
          </p>
          <div className={styles.section}>
            <div className={styles.nameContainer}>
              <InputV2
                maxLength={120}
                label={t('education-demographic-detail.first-name')}
                placeholder={t('education-demographic-detail.add-first-name')}
                value={candidateInfo?.first_name}
                onChange={(event) => {
                  const first_name = event.target.value;
                  setCandidateInfo((candidateInfo) => ({
                    ...candidateInfo,
                    first_name,
                  }));
                }}
                className={styles.input}
                labelClassName={styles.inputLabel}
              />
              <InputV2
                maxLength={120}
                label={t('education-demographic-detail.last-name')}
                placeholder={t('education-demographic-detail.add-last-name')}
                value={candidateInfo?.last_name}
                onChange={(event) => {
                  const last_name = event.target.value;

                  setCandidateInfo((candidateInfo) => ({
                    ...candidateInfo,
                    last_name,
                  }));
                }}
                className={styles.input}
                labelClassName={styles.inputLabel}
              />
            </div>
            <InputDropdownCombo
              label={t('education-demographic-detail.university')}
              placeholder={t('education-demographic-detail.add-university')}
              options={universities}
              value={candidateInfo?.university_name || ''}
              onChange={(value) => onUniversitySelect(value)}
              isValid={true}
              showOtherOption={true}
              className={styles.question}
              scrollToDropdown={scrollToDropdownUniversity}
              ref={universityDropdownRef}
            />
            <DropdownV3
              label={t('education-demographic-detail.select-degree')}
              title={t('education-demographic-detail.degree')}
              value={candidateInfo?.degree_id || ''}
              onChange={(value) => onDegreeSelect(value)}
              options={degrees}
              isSearchable={true}
              searchPlaceholder={tCommon('search')}
              selectedValueLabel={candidateInfo?.degree_label || ''}
              isValid={true}
              className={styles.question}
              scrollToDropdown={scrollToDropdownDegree}
              ref={degreeDropdownRef}
              labelClassName={styles.inputLabel}
              selectedValueClassName={styles['selected-value']}
            />
            <DropdownV3
              label={t('education-demographic-detail.select-subject')}
              title={t('education-demographic-detail.subject')}
              value={candidateInfo?.subject_id || ''}
              onChange={(value) => onSubjectSelect(value)}
              options={subjects}
              isSearchable={true}
              searchPlaceholder={tCommon('search')}
              selectedValueLabel={candidateInfo?.subject_name || ''}
              isValid={true}
              className={styles.question}
              scrollToDropdown={scrollToDropdownSubject}
              ref={subjectDropdownRef}
              labelClassName={styles.inputLabel}
              selectedValueClassName={styles['selected-value']}
            />
            {!showDegreeName ? (
              <Tooltip
                content={t('education-demographic-detail.add-degree-tooltip')}
                position="top"
                xAlign="center"
                color="dark"
                className={styles.tooltipContainer}
                containerClassName={styles.tooltipContainerContent}
              >
                <div
                  onClick={() => setShowDegreeName(true)}
                  className={cn(styles.linkGap, styles.linkText)}
                >
                  {t('education-demographic-detail.add-specefic-degree')}
                </div>
              </Tooltip>
            ) : (
              <InputV2
                label={t('education-demographic-detail.degree-name')}
                placeholder={t('education-demographic-detail.computer-science')}
                showOptionalLabel={true}
                value={candidateInfo?.degree_name}
                onChange={(event) => {
                  setCandidateInfo((info) => ({
                    ...info,
                    degree_name: event.target.value,
                  }));
                }}
                isInvalid={false}
                infoToolTip={
                  <Tooltip
                    content={t(
                      'education-demographic-detail.add-degree-tooltip'
                    )}
                    position="top"
                    xAlign="center"
                    color="dark"
                    className={styles.tooltipContainer}
                    containerClassName={styles.tooltipContainerContent}
                  >
                    <Icon iconName="icon_info" size="xsmall" />
                  </Tooltip>
                }
                className={styles.question}
                labelClassName={styles.inputLabel}
              />
            )}

            <DropdownV3
              label={t('education-demographic-detail.select-graduation-year')}
              title={t('education-demographic-detail.graduation-year')}
              value={candidateInfo?.graduation_year?.toString() || ''}
              ref={graduateYearDropdownRef}
              onChange={(value) => {
                setCandidateInfo((info) => ({
                  ...info,
                  graduation_year: parseInt(value),
                }));
              }}
              options={graduateDateOptions}
              searchPlaceholder={tCommon('search')}
              selectedValueLabel={candidateInfo?.graduation_year?.toString()}
              isValid={true}
              className={styles.question}
              labelClassName={styles.inputLabel}
              dropdownClassName={styles.options}
            />
          </div>
          <div className={styles['bottom-ref']} ref={bottomRef}></div>
        </div>

        <Footer
          isBackLoading={isBackLoading}
          isReady={isReady}
          isLoading={isLoading}
          onSubmit={onSubmit}
          isFullyScrolled={fullyScrolled}
          isWindowScrolled={isWindowScrolled}
        />
      </div>
    );
  }
);

Education.displayName = 'Education';
